import { FC, useState } from 'react'
import { BaseProps } from '@/types/common'
import FloatingInput from '@/components/ui/FloatingInput'
import { useAuth } from '@/hooks'
import { i18n } from '@/lang'

interface LinkAccountDialogProps extends BaseProps {
  onClose: () => void
}

const LinkAccountDialog: FC<LinkAccountDialogProps> = ({ lang, onClose }) => {
  const { linkingPending, confirmLinkAccount, dismissLinking } = useAuth()
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const t = i18n[lang]

  if (!linkingPending) return null

  const emailDisplay = linkingPending.email
  const message = t.linkAccountMessage.replace('{email}', emailDisplay)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!password) return
    setLoading(true)
    setError('')
    try {
      await confirmLinkAccount(password)
      onClose()
    } catch {
      setError(t.linkingFailed)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    dismissLinking()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-[60] flex items-center justify-center p-4">
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white w-full max-w-[400px] rounded-none shadow-lg border border-gray-200 px-8 py-8 flex flex-col gap-5"
      >
        <h2 className="text-xl font-medium">{t.linkAccountTitle}</h2>
        <p className="text-sm text-gray-600 leading-relaxed">{message}</p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <FloatingInput
              label={t.password}
              type="password"
              value={password}
              onChange={setPassword}
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-sm">{error}</div>
          )}

          <div className="flex gap-3 mt-2">
            <button
              type="submit"
              disabled={loading || !password}
              className="flex-1 h-11 bg-black text-white text-sm font-medium tracking-wide hover:bg-neutral-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? '...' : t.linkAccounts}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 h-11 border border-gray-300 text-sm font-medium text-gray-600 hover:border-gray-500 hover:text-black transition-colors"
            >
              {t.cancel}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default LinkAccountDialog
